// Étape 17 — Admin : Commissions à valider, clôture du mois, relevés, export, rapport de recalcul
import { useState, useEffect } from "react";
import axios from "axios";
import { C, Btn, Input, Alert, Spinner, fmtEur, fdate, authDownload, errMsg } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hdr = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("auprea_token")}` } });
const moisCourant = () => new Date().toISOString().slice(0, 7);
const TYPES = { directe: "Directe", override_n1: "N1", override_n2: "N2", override_n3: "N3", prime_pr_ventes: "Prime PR" };

const Ligne = ({ e, onDone }) => {
  const [mode, setMode] = useState(null);
  const [v, setV] = useState({ montant: e.montant_encaisse, motif: "", commentaire: "" });
  const [err, setErr] = useState("");
  const post = async (path, body) => { setErr(""); try { await axios.post(`${API}/admin/commissions/encaissements/${e.id}/${path}`, body, hdr()); setMode(null); onDone(); } catch (x) { setErr(errMsg(x)); } };
  const ecart = Math.abs(e.ecart) >= 0.01;
  return (
    <div data-testid={`encaissement-${e.id}`} style={{ background: "#fff", border: `.5px solid ${C.border}`, borderLeft: `3px solid ${ecart ? C.red : e.statut === "en_pause" ? C.amber : C.gold}`, borderRadius: 12, padding: 14, marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>{e.client} <span style={{ fontWeight: 400, color: C.mid, fontSize: 12 }}>· {e.consultant_nom} · {fdate(e.date)} · {e.facture}</span></div>
          <div style={{ fontSize: 12, color: C.mid }}>{(e.prestations || []).join(", ")} · {e.moyen === "virement" ? "Virement" : "Carte (Stripe)"}</div>
          {e.statut === "en_pause" && <div style={{ fontSize: 12, color: C.amber }}>En attente : {e.commentaire || "—"}</div>}
        </div>
        <div style={{ textAlign: "right", fontSize: 12 }}>
          <div>Attendu : <strong>{fmtEur(e.montant_attendu)}</strong></div>
          <div data-testid={`encaissement-recu-${e.id}`} style={{ background: ecart ? C.redlt : "transparent", color: ecart ? C.red : C.text, padding: "0 4px", borderRadius: 4 }}>Encaissé : <strong>{fmtEur(e.montant_encaisse)}</strong>{ecart && ` (écart ${fmtEur(e.ecart)})`}</div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "10px 0" }}>
        {e.commissions.map(c => <span key={c.id} style={{ fontSize: 11, background: C.gray2, borderRadius: 6, padding: "3px 8px" }}>{TYPES[c.type] || c.type} · {c.beneficiaire} · {Math.round((c.taux || 0) * 1000) / 10} % · <strong>{fmtEur(c.montant)}</strong></span>)}
      </div>
      {err && <div style={{ color: C.red, fontSize: 12 }}>{err}</div>}
      {mode === "corriger" && (
        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          <Input type="number" step="0.01" value={v.montant} onChange={(x) => setV({ ...v, montant: x.target.value })} style={{ width: 130 }} data-testid="corriger-montant" />
          <Input value={v.motif} onChange={(x) => setV({ ...v, motif: x.target.value })} placeholder="Motif (obligatoire)" data-testid="corriger-motif" />
          <Btn small variant="gold" disabled={!v.motif.trim()} onClick={() => post("corriger", { montant: Number(v.montant), motif: v.motif })} data-testid="corriger-valider">Recalculer</Btn>
        </div>
      )}
      {mode === "attente" && (
        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          <Input value={v.commentaire} onChange={(x) => setV({ ...v, commentaire: x.target.value })} placeholder="Commentaire" data-testid="attente-commentaire" />
          <Btn small onClick={() => post("attente", { commentaire: v.commentaire })} data-testid="attente-valider">Mettre en attente</Btn>
        </div>
      )}
      <div style={{ display: "flex", gap: 6 }}>
        <Btn small variant="gold" onClick={() => post("confirmer", {})} data-testid={`confirmer-${e.id}`}>Confirmer</Btn>
        <Btn small onClick={() => setMode(mode === "corriger" ? null : "corriger")} data-testid={`corriger-${e.id}`}>Corriger le montant encaissé</Btn>
        <Btn small onClick={() => setMode(mode === "attente" ? null : "attente")} data-testid={`attente-${e.id}`}>Mettre en attente</Btn>
      </div>
    </div>
  );
};

const Cloture = () => {
  const [mois, setMois] = useState(moisCourant());
  const [rel, setRel] = useState([]);
  const [msg, setMsg] = useState("");
  const [dates, setDates] = useState({});
  const load = () => axios.get(`${API}/admin/commissions/releves`, { ...hdr(), params: { mois } }).then(r => setRel(r.data));
  useEffect(() => { load(); }, [mois]); // eslint-disable-line react-hooks/exhaustive-deps
  const cloturer = async () => {
    if (!window.confirm(`Clôturer ${mois} et générer les relevés ?`)) return;
    const r = await axios.post(`${API}/admin/commissions/cloturer`, { mois }, hdr());
    setMsg(`${r.data.releves.length} relevé(s) généré(s)`); load();
  };
  const verser = async (id) => { await axios.post(`${API}/admin/commissions/releves/${id}/versement`, { date: dates[id] || new Date().toISOString().slice(0, 10) }, hdr()); load(); };
  return (
    <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, padding: 16, marginTop: 20 }} data-testid="cloture-mois">
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>Clôture du mois</div>
        <Input type="month" value={mois} onChange={(e) => setMois(e.target.value)} style={{ width: 160 }} data-testid="cloture-mois-input" />
        <Btn variant="navy" onClick={cloturer} data-testid="cloturer-btn">Clôturer et générer les relevés</Btn>
        <Btn onClick={() => authDownload(`/admin/commissions/export.csv?mois=${mois}`, `commissions_${mois}.csv`)} data-testid="commissions-export-csv">⬇ Export CSV comptable</Btn>
        {msg && <span style={{ fontSize: 12, color: C.green }}>{msg}</span>}
      </div>
      {rel.map(r => (
        <div key={r.id} data-testid={`releve-${r.id}`} style={{ display: "flex", gap: 10, alignItems: "center", padding: "8px 0", borderTop: `.5px solid ${C.border}`, fontSize: 12, flexWrap: "wrap" }}>
          <strong style={{ minWidth: 120 }}>{r.numero}</strong><span style={{ minWidth: 160 }}>{r.consultant_nom}</span><strong>{fmtEur(r.total)}</strong>
          <Btn small onClick={() => authDownload(`/commissions/releves/${r.id}/pdf`, `${r.numero}.pdf`, true)}>PDF</Btn>
          {r.statut === "verse" ? <span style={{ color: C.green }}>✓ Versé le {fdate(r.verse_le)}</span> : (
            <>
              <Input type="date" value={dates[r.id] || ""} onChange={(e) => setDates({ ...dates, [r.id]: e.target.value })} style={{ width: 150 }} data-testid={`versement-date-${r.id}`} />
              <label style={{ display: "flex", gap: 4, alignItems: "center", cursor: "pointer" }}><input type="checkbox" onChange={(e) => e.target.checked && verser(r.id)} data-testid={`versement-${r.id}`} /> Versement effectué</label>
            </>
          )}
        </div>
      ))}
      {!rel.length && <div style={{ fontSize: 12, color: C.muted }}>Aucun relevé pour ce mois.</div>}
    </div>
  );
};

const Rapport = () => {
  const [r, setR] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { axios.get(`${API}/admin/commissions/recalcul/rapport`, hdr()).then(x => setR(x.data)); }, []);
  if (!r?.at) return null;
  return (
    <div style={{ marginTop: 20, fontSize: 12, color: C.mid }} data-testid="rapport-recalcul">
      <button onClick={() => setOpen(!open)} style={{ background: "none", border: "none", color: C.navy, cursor: "pointer", fontWeight: 700, padding: 0, fontFamily: "inherit" }}>
        {open ? "▾" : "▸"} Rapport du recalcul complet ({fdate(r.at)}) — {r.factures_traitees} facture(s), {r.ecarts.length} écart(s), anciennes commissions leads supprimées : {fmtEur(r.total_anciennes_leads)}
      </button>
      {open && (
        <div style={{ marginTop: 8, background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 10, padding: 12 }}>
          <div>Avant : {r.commissions_avant} ligne(s), {fmtEur(r.total_avant)} · Après : {r.commissions_apres} ligne(s), {fmtEur(r.total_apres)}</div>
          {r.ecarts.map((e, i) => <div key={i}>{e.beneficiaire} · {e.parcours} · {e.type} : {fmtEur(e.avant)} → {fmtEur(e.apres)} ({e.ecart > 0 ? "+" : ""}{fmtEur(e.ecart)})</div>)}
          {r.anciennes_commissions_leads.map(l => <div key={l.id}>Lead {l.id} ({l.prospect}) : {fmtEur(l.comm)} supprimés</div>)}
        </div>
      )}
    </div>
  );
};

export default function CommissionsValider() {
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const load = () => axios.get(`${API}/admin/commissions/a-valider`, hdr()).then(r => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <Spinner />;
  const toutConfirmer = async () => { const r = await axios.post(`${API}/admin/commissions/tout-confirmer`, {}, hdr()); setMsg(`${r.data.confirmes} encaissement(s) confirmé(s)`); load(); };
  return (
    <div data-testid="commissions-a-valider">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 13, color: C.mid }}>Encaissements dont les commissions attendent votre confirmation du montant réellement encaissé.</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {msg && <span style={{ fontSize: 12, color: C.green }}>{msg}</span>}
          <Btn variant="gold" onClick={toutConfirmer} disabled={!data.items.length} data-testid="tout-confirmer">Tout confirmer (sans écart)</Btn>
        </div>
      </div>
      {!data.items.length && <Alert type="info"><span>✓</span><span data-testid="a-valider-vide">Aucune commission à valider.</span></Alert>}
      {data.items.map(e => <Ligne key={e.id} e={e} onDone={load} />)}
      <Cloture />
      <Rapport />
    </div>
  );
}
